import React, { ChangeEvent, createContext, FC, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  FormGenerator,
  FormInput,
  ModalWrapper,
  Switch,
} from '@microservices/wiskey-react-components'
import { FormInputsType } from '@microservices/wiskey-react-components/dist/types'
import { Box, Grid, Typography } from '@mui/material'

import { VariableType } from '@pages/ContextMenuCreateOrEdit/ContextMenuCreateOrEdit'

import { CommandParameters } from '@components/CommandParameters'
import { getStateFromData } from '@components/CommandParameters/helpers'
import { ModalFormContainer } from '@components/ModalFormContainer'
import { ScriptValueDialog } from '@components/ScriptValueDialog'
import { SwitchTable } from '@components/SwitchTable'

import { useFetchAllGanttQuery, useFetchAllViewQuery } from '@redux/api'
import { useFetchAllWindowsQuery } from '@redux/api/windows.api'

import { useCommands } from '@hooks'
import { isObjectSource } from '@helpers'
import { checkValidJS, getEntityAutocompleteOptionsFromType } from '@helpers'
import {
  CONTEXT_MENU_APPLICABLE_TO,
  CONTEXT_MENU_POINT_ACTION_COLUMNS,
  GENERATOR_INPUT_TYPE,
  MAX_INPUT_LENGTH,
  MENU_POINT_TYPES,
  MODAL_TYPE,
  OBJECT_TYPES_FOR_MENU_POINT_LABEL,
  OBJECT_TYPES_FOR_MENU_POINT_VALUE,
  SOURCE_SELECTION_OPTIONS,
  TYPES_SOURCE_OPTIONS,
} from '@constants'
import {
  ActionRow,
  AutocompleteOption,
  CommandParametersType,
  CommandType,
  ContextMenuAddMenuPointDialogContextType,
  TreeItemMenuPointOptionType,
} from '@types'

import { useMenuPointActionsHandlers } from '../../hooks/useMenuPointActionsHandlers'

type AddMenuPointDialogProps = {
  objectCode: string | undefined
  objectTitle: string | undefined
  treeNames: string[]
  item?: TreeItemMenuPointOptionType | null
  variables?: VariableType[]
  isLoadingObjects: boolean
  isLoadingSystems: boolean
  objectsOptions: AutocompleteOption[]
  systemsOptions: AutocompleteOption[]
  applicableTo: AutocompleteOption<CONTEXT_MENU_APPLICABLE_TO> | null
  onSetConfigCommandInternalId: (value: string | null) => void
  onClose: () => void
  onSave: (data: AddMenuPointForm) => void
}

export type AddMenuPointForm = {
  title: string
  jsTitle: boolean
  titleJsValue: string
  menuPointType: AutocompleteOption | null
  objectType: AutocompleteOption<string> | null
  command: CommandType | null
  commandParameters: CommandParametersType[]
  commandBody: CommandParametersType[]
  value?: AutocompleteOption | null
  conditionsDisabled: string
  conditionsHidden: string
  actions: ActionRow[]
  // Флаги для таймлайна
  showOnSelection?: boolean // Показывать ли пункт меню, если он открыт по выделенной области
  useCommandOnEvent?: boolean // Применять команду ко всей ячейке или к отдельному событию в ней
}

const OBJECT_TYPES_FOR_MENU_POINT_OPTIONS = [
  {
    id: OBJECT_TYPES_FOR_MENU_POINT_VALUE.GANTT_CHART,
    label: OBJECT_TYPES_FOR_MENU_POINT_LABEL.GANTT_CHART,
  },
  { id: OBJECT_TYPES_FOR_MENU_POINT_VALUE.VIEW, label: OBJECT_TYPES_FOR_MENU_POINT_LABEL.VIEW },
  { id: OBJECT_TYPES_FOR_MENU_POINT_VALUE.WINDOW, label: OBJECT_TYPES_FOR_MENU_POINT_LABEL.WINDOW },
]

const MENU_POINT_TYPES_OPTIONS = [
  { id: MENU_POINT_TYPES.COMMAND, label: MENU_POINT_TYPES.COMMAND },
  { id: MENU_POINT_TYPES.LINK, label: MENU_POINT_TYPES.LINK },
  { id: MENU_POINT_TYPES.HEADER, label: MENU_POINT_TYPES.HEADER },
  { id: MENU_POINT_TYPES.DIVIDER, label: MENU_POINT_TYPES.DIVIDER },
  { id: MENU_POINT_TYPES.GROUP, label: MENU_POINT_TYPES.GROUP },
  { id: MENU_POINT_TYPES.ACTION, label: MENU_POINT_TYPES.ACTION },
]

export const PageContext = createContext({} as ContextMenuAddMenuPointDialogContextType)

export const AddMenuPointDialog: FC<AddMenuPointDialogProps> = ({
  objectCode,
  objectTitle,
  item,
  treeNames,
  variables,
  isLoadingObjects,
  isLoadingSystems,
  objectsOptions,
  systemsOptions,
  onSetConfigCommandInternalId,
  onClose,
  onSave,
  applicableTo,
}) => {
  const { t } = useTranslation()

  const [isShowScriptValueDialogTitle, setShowScriptValueDialogTitle] = useState(false)
  const [currentNameScriptDialog, setCurrentNameScriptDialog] = useState<
    'titleJsValue' | 'conditionsDisabled' | 'conditionsHidden' | null
  >(null)

  const methods = useForm<AddMenuPointForm>({
    defaultValues: {
      title: '',
      titleJsValue: '',
      jsTitle: false,
      menuPointType: null,
      objectType: null,
      value: null,
      commandParameters: [],
      commandBody: [],
      command: null,
      conditionsDisabled: '',
      conditionsHidden: '',
      actions: [],
      showOnSelection: false,
      useCommandOnEvent: false,
    },
  })

  const {
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { isDirty },
  } = methods

  const watchedMenuPointType = watch('menuPointType')
  const watchedJs = watch('jsTitle')
  const watchedObjectType = watch('objectType')
  const watchedValue = watch('value')
  const watchedSourceCommand = watch('command.sourceCommand')
  const watchedSourceType = watch('command.sourceType')
  const watchedActions = watch('actions')
  const watchUseCommandOnEvent = watch('useCommandOnEvent')

  const isLink = watchedMenuPointType?.id === MENU_POINT_TYPES.LINK
  const isDivider = watchedMenuPointType?.id === MENU_POINT_TYPES.DIVIDER
  const isHeader = watchedMenuPointType?.id === MENU_POINT_TYPES.HEADER
  const isGroup = watchedMenuPointType?.id === MENU_POINT_TYPES.GROUP
  const isCommand = watchedMenuPointType?.id === MENU_POINT_TYPES.COMMAND
  const isAction = watchedMenuPointType?.id === MENU_POINT_TYPES.ACTION
  const isGantt = watchedObjectType?.id === OBJECT_TYPES_FOR_MENU_POINT_VALUE.GANTT_CHART
  const isView = watchedObjectType?.id === OBJECT_TYPES_FOR_MENU_POINT_VALUE.VIEW
  const isWindow = watchedObjectType?.id === OBJECT_TYPES_FOR_MENU_POINT_VALUE.WINDOW
  const isUiMenuType = isDivider || isHeader || isGroup

  const {
    data: ganttEntities,
    isLoading: isLoadingGanttEntities,
    isFetching: isFetchingGanttEntities,
  } = useFetchAllGanttQuery({}, { skip: !isGantt })

  const {
    data: entities,
    isLoading: isLoadingEntities,
    isFetching: isFetchingEntities,
  } = useFetchAllViewQuery({}, { skip: !isView })

  const {
    data: windows,
    isLoading: isLoadingWindowEntities,
    isFetching: isFetchingWindowEntities,
  } = useFetchAllWindowsQuery({}, { skip: !isWindow })

  const loadingEntities =
    isLoadingEntities ||
    isFetchingEntities ||
    isLoadingGanttEntities ||
    isFetchingGanttEntities ||
    isLoadingWindowEntities ||
    isFetchingWindowEntities

  const autocompleteValueOptions = useMemo(
    () =>
      getEntityAutocompleteOptionsFromType(watchedObjectType?.id, {
        [OBJECT_TYPES_FOR_MENU_POINT_VALUE.VIEW]: entities?.data,
        [OBJECT_TYPES_FOR_MENU_POINT_VALUE.GANTT_CHART]: ganttEntities,
        [OBJECT_TYPES_FOR_MENU_POINT_VALUE.WINDOW]: windows,
      }),
    [watchedObjectType?.id, entities?.data, ganttEntities, windows]
  )

  const { commandOptions, commandsSystem, commandsObject } = useCommands({
    sourceCommand: watchedSourceCommand?.id,
    currentSourceType: watchedSourceType?.label as TYPES_SOURCE_OPTIONS,
  })

  const actionsHandlers = useMenuPointActionsHandlers({
    actions: watchedActions,
    setValue,
    objectCode: objectCode as string,
  })

  const {
    handlers: { handleOpenAddActionDialog, handleDeleteAction },
    state: { showAddActionDialog },
  } = actionsHandlers

  const sourceCommandValueSystemsOnEdit = systemsOptions
    ? systemsOptions.find(option => {
        return option.id === item?.data?.command?.objectCode
      })
    : null

  const sourceCommandValueObjectsOnEdit = objectsOptions
    ? objectsOptions.find(option => {
        return option.id === item?.data?.command?.objectCode
      })
    : null

  useEffect(() => {
    if (item) {
      reset({
        ...item.data,
        menuPointType: item.data.menuPointType
          ? { id: item.data.menuPointType, label: item.data.menuPointType }
          : null,
        objectType: item.data.objectType
          ? { id: item.data.objectType, label: item.data.objectType }
          : null,
        value: item.data.command?.name
          ? {
              id: item.data.command.name,
              label: item.data.command.name,
              httpMethod: item.data.command.httpMethod,
            }
          : {
              id: item.data.value,
              label: item.data.valueTitle,
            },
        commandParameters: getStateFromData(item.data.commandParameters),
        commandBody: getStateFromData(item.data.commandBody),
        command: item.data.command?.resource
          ? {
              ...item.data.command,
              objectCode: isObjectSource(item.data.command.resource)
                ? sourceCommandValueObjectsOnEdit
                : sourceCommandValueSystemsOnEdit,
              sourceCommand: isObjectSource(item.data.command.resource)
                ? sourceCommandValueObjectsOnEdit
                : sourceCommandValueSystemsOnEdit,
              sourceType: item.data.command.resource
                ? { id: item.data.command.resource, label: item.data.command.resource }
                : null,
            }
          : null,
      })
    }

    if (item && item.data.menuPointType === MENU_POINT_TYPES.COMMAND) {
      onSetConfigCommandInternalId(item.data.value ?? null)
    }
  }, [item])

  useEffect(() => {
    if (isUiMenuType) {
      setValue('objectType', null, { shouldDirty: true })
      setValue('value', null, { shouldDirty: true })
    }
  }, [isUiMenuType])

  useEffect(() => {
    if (isDivider) {
      setValue('jsTitle', false, { shouldDirty: true })
    }
  }, [isDivider])

  const handleSave = (data: AddMenuPointForm) => {
    onSave(data)
  }

  const handleClose = () => {
    if (isDirty && !confirm(t('notifications.leave'))) {
      return
    }

    onClose()
  }

  const titleJsValue: FormInputsType = {
    name: 'titleJsValue',
    inputType: GENERATOR_INPUT_TYPE.TEXTAREA,
    placeholder: t('contextMenuCreate.addMenuPointModal.titleJsValue.placeholder'),
    label: t('contextMenuCreate.addMenuPointModal.titleJsValue.label'),
    maxLengthInput: MAX_INPUT_LENGTH,
    readOnly: true,
    labelSx: { minWidth: 150, width: 150 },
    rules: {
      required: true,
      maxLength: MAX_INPUT_LENGTH,
    },
    additionalBtn: {
      isEnabled: true,
      text: 'edit',
      color: 'primary',
      variant: 'contained',
      onClick: () => {
        setCurrentNameScriptDialog('titleJsValue')
        setShowScriptValueDialogTitle(true)
      },
    },
  }

  const clearParamsAndBody = () => {
    setValue('commandParameters', [])
    setValue('commandBody', [])
  }

  const handleCloseScriptDialog = () => {
    setCurrentNameScriptDialog(null)
    setShowScriptValueDialogTitle(false)
  }

  const handleSaveScriptDialog = (value: string) => {
    if (currentNameScriptDialog) {
      setValue(currentNameScriptDialog, value, { shouldDirty: true })
    }
  }

  const handleChangeSourceType = (newSourceValue: AutocompleteOption) => {
    if (newSourceValue?.id === watchedSourceType?.id) return

    setValue('command.sourceType', newSourceValue)
    setValue('command.sourceCommand', null)
    setValue('value', null)

    clearParamsAndBody()
  }

  const handleChangeMenuPointType = (value: AutocompleteOption) => {
    setValue('objectType', null, { shouldDirty: true })
    setValue('value', null, { shouldDirty: true })
    setValue('commandParameters', [], { shouldDirty: true })
    setValue('commandBody', [], { shouldDirty: true })
    setValue('command.sourceType', null)
    setValue('command.sourceCommand', null, { shouldDirty: true })

    if (value && value.id === MENU_POINT_TYPES.COMMAND) {
      const sourceTypeValue = SOURCE_SELECTION_OPTIONS.find(
        option => option.label === TYPES_SOURCE_OPTIONS.OBJECT
      )

      setValue('objectType', value as AutocompleteOption<string>)
      setValue('command.sourceType', sourceTypeValue)

      if (isObjectSource(watchedSourceType?.label)) {
        setValue(
          'command.sourceCommand',
          objectsOptions?.find(object => object.id === objectCode)
        )
      }
    }
  }

  const handleChangeUseCommandOnEvent = (event: ChangeEvent<HTMLInputElement>) => {
    setValue('useCommandOnEvent', event.target.checked, { shouldDirty: true })
  }

  const handleChangeSourceCommand = (value: AutocompleteOption) => {
    if (value?.id === watchedSourceCommand?.id) return

    setValue('command', {
      ...getValues('command'),
      sourceCommand: value,
      objectCode: value,
    })
    setValue('value', null)

    clearParamsAndBody()
  }

  const handleChangeCommandValue = (value: AutocompleteOption) => {
    if (value?.id === watchedValue?.id) return

    if (value?.label) {
      const commands = isObjectSource(watchedSourceType?.label) ? commandsObject : commandsSystem
      const command = commands?.find(cmd => cmd.code === value.label)

      setValue(
        'command',
        {
          ...getValues().command,
          objectCode: getValues().command?.sourceCommand ?? null,
          id: getValues().command?.id ?? null,
          name: command!.code,
          resource: watchedSourceType?.label as TYPES_SOURCE_OPTIONS,
          httpMethod: command!.httpMethod,
        },
        { shouldDirty: true }
      )
    }

    clearParamsAndBody()
  }

  const isCurrentTreeName = (value: string) =>
    Boolean(item?.data.title.toLowerCase() === value.toLowerCase())

  const isNameUsed = (value: string) =>
    !treeNames.includes(value.toLowerCase()) || isCurrentTreeName(value.toLowerCase())

  return (
    <PageContext.Provider value={actionsHandlers}>
      <ModalWrapper
        hideDivider
        disabledSave={!isDirty}
        isShow={true}
        title={t('contextMenuCreate.addMenuPointModal.titleModal')}
        width={1200}
        containerStyle={{
          px: 0.5,
          py: 0.5,
          // borderRadius: 0
        }}
        onClose={handleClose}
        onSave={handleSubmit(handleSave)}
      >
        <ModalFormContainer maxHeight={600}>
          {isShowScriptValueDialogTitle && (
            <ScriptValueDialog
              windowBoundaries
              isShow={isShowScriptValueDialogTitle}
              modalType={MODAL_TYPE.EDIT}
              objectFields={[]}
              value={currentNameScriptDialog ? watch(currentNameScriptDialog) : ''}
              validator={value =>
                checkValidJS(
                  value,
                  { srcObj: {}, global: {}, local: {} },
                  { local: variables?.map(({ field }) => field) || [], global: [] }
                )
              }
              onClose={handleCloseScriptDialog}
              onSave={handleSaveScriptDialog}
            />
          )}
          <Box pb={2} pt={2} px={2}>
            <FormProvider {...methods}>
              <FormGenerator
                inputs={[
                  {
                    name: 'rowTitle',
                    inputs: [
                      {
                        name: 'title',
                        inputType: GENERATOR_INPUT_TYPE.INPUT,
                        label: t('contextMenuCreate.addMenuPointModal.title.label'),
                        placeholder: t('contextMenuCreate.addMenuPointModal.title.placeholder'),
                        rules: {
                          required: true,
                          validate: value => {
                            if (!isNameUsed(value) && value.trim().length !== 0) {
                              return t('contextMenuCreate.addMenuPointModal.title.errors.used', {
                                name: value,
                              })
                            }

                            if (value.trim().length === 0) {
                              return false
                            }
                          },
                        },
                        labelSx: { minWidth: 150 },
                      },
                      {
                        name: 'jsTitle',
                        inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
                        label: t('contextMenuCreate.addMenuPointModal.jsTitle.label'),
                        labelPlacement: 'end',
                        disabled: isDivider,
                        labelSx: { minWidth: 30, width: 30 },
                      },
                    ],
                  },
                  ...(watchedJs ? [titleJsValue] : []),
                ]}
              />
              <Box display='flex' mb={isCommand || isLink ? 1 : 3} mt={0}>
                <FormInput
                  autocompleteOptions={MENU_POINT_TYPES_OPTIONS}
                  inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
                  label={t('contextMenuCreate.addMenuPointModal.menuPointType.label')}
                  labelSx={{ minWidth: 150 }}
                  name={'menuPointType'}
                  placeholder={t('contextMenuCreate.addMenuPointModal.menuPointType.placeholder')}
                  rules={{ required: true }}
                  onChangeAutocomplete={handleChangeMenuPointType}
                />
              </Box>
              {isCommand && applicableTo?.id === CONTEXT_MENU_APPLICABLE_TO.TIME_TABLE && (
                <>
                  <Typography variant='h6'>{t('contextMenuCreate.timelineOptions')}</Typography>
                  <Box alignItems={'center'} display='flex'>
                    <FormInput
                      inputType={GENERATOR_INPUT_TYPE.CHECKBOX}
                      label={t('contextMenuCreate.addMenuPointModal.showOnSelection.label')}
                      labelSx={{ minWidth: 150, width: 150 }}
                      name={'showOnSelection'}
                      sx={{
                        mr: 4,
                      }}
                    />
                    <Box alignItems='center' display={'flex'} mb={1}>
                      <Typography>Apply on Cell</Typography>
                      <Switch
                        checked={watchUseCommandOnEvent}
                        name={'useCommandOnEvent'}
                        onChange={handleChangeUseCommandOnEvent}
                      />
                      <Typography>Apply on Event</Typography>
                    </Box>
                  </Box>
                </>
              )}

              {!isUiMenuType && (
                <Grid container>
                  <FormGenerator
                    mainGridSpacing={0.25}
                    inputs={[
                      ...(isLink
                        ? [
                            {
                              name: 'objectType',
                              inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
                              autocompleteOptions: OBJECT_TYPES_FOR_MENU_POINT_OPTIONS,
                              label: t('contextMenuCreate.addMenuPointModal.objectType.label'),
                              placeholder: t(
                                'contextMenuCreate.addMenuPointModal.objectType.placeholder'
                              ),
                              labelSx: { minWidth: 150 },
                              rules: {
                                required: isLink,
                              },
                              onChangeAutocomplete: () => {
                                setValue('value', null)
                              },
                            },
                            {
                              name: 'value',
                              inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
                              loading: loadingEntities,
                              autocompleteOptions:
                                watchedMenuPointType?.id === MENU_POINT_TYPES.LINK
                                  ? autocompleteValueOptions
                                  : [],
                              label: t('contextMenuCreate.addMenuPointModal.value.label'),
                              placeholder: t(
                                'contextMenuCreate.addMenuPointModal.value.placeholder'
                              ),
                              labelSx: { minWidth: 150 },
                              formLabelSx: { pb: 2, pt: 0 },
                              rules: {
                                required: isLink,
                              },
                            },
                          ]
                        : []),
                      ...(isCommand
                        ? [
                            {
                              name: 'rowSource',
                              inputs: [
                                {
                                  name: 'command.sourceType',
                                  inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
                                  autocompleteOptions: SOURCE_SELECTION_OPTIONS,
                                  label: 'Source',
                                  placeholder: t('placeholder.selectSourceType'),
                                  labelSx: { minWidth: 150 },
                                  rules: { required: isCommand },
                                  disableClearable: true,
                                  onChangeAutocomplete: (value: AutocompleteOption) =>
                                    handleChangeSourceType(value),
                                },
                                {
                                  name: 'command.sourceCommand',
                                  inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
                                  autocompleteOptions: isObjectSource(watchedSourceType?.label)
                                    ? objectsOptions
                                    : systemsOptions,
                                  label: '',
                                  placeholder: t('placeholder.selectSourceCommand'),
                                  labelSx: { minWidth: 150 },
                                  rules: { required: isCommand },
                                  onChangeAutocomplete: (value: AutocompleteOption) =>
                                    handleChangeSourceCommand(value),
                                },
                              ],
                            },
                            {
                              name: 'value',
                              inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
                              loading: isLoadingObjects || isLoadingSystems,
                              autocompleteOptions: watchedSourceCommand ? commandOptions : [],
                              label: t('label.commandCode'),
                              placeholder: t('placeholder.selectCommandCode'),
                              labelSx: { minWidth: 150 },
                              formLabelSx: { pb: 0, pt: 0 },
                              rules: { required: isCommand },
                              onChangeAutocomplete: (value: AutocompleteOption) =>
                                handleChangeCommandValue(value),
                            },
                          ]
                        : []),
                    ]}
                  />
                  {isCommand && (
                    <Grid display={'flex'} flexDirection={'column'} gap={1} mb={3}>
                      <CommandParameters
                        commandCode={watchedValue?.label as string}
                        containerSx={{ mb: 0 }}
                        fieldsObjectCode={objectCode ?? ''}
                        fieldsObjectTitle={objectTitle ?? ''}
                        label={t('contextMenuCreate.addMenuPointModal.parameters.label')}
                        name='commandParameters'
                        parametersMode='params'
                        sourceCode={watchedSourceCommand?.id as string}
                        sourceTitle={watchedSourceCommand?.label as string}
                        typeSource={watchedSourceType?.id as TYPES_SOURCE_OPTIONS}
                      />
                      <CommandParameters
                        commandCode={watchedValue?.label as string}
                        containerSx={{ mb: 0 }}
                        fieldsObjectCode={objectCode ?? ''}
                        fieldsObjectTitle={objectTitle ?? ''}
                        label={t('contextMenuCreate.addMenuPointModal.body.label')}
                        name='commandBody'
                        parametersMode='body'
                        sourceCode={watchedSourceCommand?.id as string}
                        sourceTitle={watchedSourceCommand?.label as string}
                        typeSource={watchedSourceType?.id as TYPES_SOURCE_OPTIONS}
                      />
                    </Grid>
                  )}
                  <FormGenerator
                    inputs={[
                      {
                        name: 'conditionsDisabled',
                        label: t('contextMenuCreate.addMenuPointModal.conditionsDisabled.label'),
                        placeholder: t(
                          'contextMenuCreate.addMenuPointModal.conditionsDisabled.placeholder'
                        ),
                        inputType: GENERATOR_INPUT_TYPE.TEXTAREA,
                        maxLengthInput: MAX_INPUT_LENGTH,
                        readOnly: true,
                        labelSx: { minWidth: 150 },
                        formLabelSx: { pr: 2 },
                        rules: {
                          maxLength: MAX_INPUT_LENGTH,
                        },
                        additionalBtn: {
                          isEnabled: true,
                          text: 'edit',
                          color: 'primary',
                          variant: 'contained',
                          onClick: () => {
                            setCurrentNameScriptDialog('conditionsDisabled')
                            setShowScriptValueDialogTitle(true)
                          },
                        },
                        rows: 1,
                        sx: {
                          '& .MuiInputBase-root': {
                            py: '8.5px',
                          },
                        },
                      },
                      {
                        name: 'conditionsHidden',
                        label: t('contextMenuCreate.addMenuPointModal.conditionsHidden.label'),
                        placeholder: t(
                          'contextMenuCreate.addMenuPointModal.conditionsHidden.placeholder'
                        ),
                        inputType: GENERATOR_INPUT_TYPE.TEXTAREA,
                        maxLengthInput: MAX_INPUT_LENGTH,
                        readOnly: true,
                        labelSx: { minWidth: 150 },
                        formLabelSx: { pr: 2 },
                        rules: {
                          maxLength: MAX_INPUT_LENGTH,
                        },
                        additionalBtn: {
                          isEnabled: true,
                          text: 'edit',
                          color: 'primary',
                          variant: 'contained',
                          onClick: () => {
                            setCurrentNameScriptDialog('conditionsHidden')
                            setShowScriptValueDialogTitle(true)
                          },
                        },
                        rows: 1,
                        sx: {
                          '& .MuiInputBase-root': {
                            py: '8.5px',
                          },
                        },
                      },
                    ]}
                    sx={{
                      '& .MuiGrid-item': {
                        '&:nth-last-child(-n+2)': {
                          pt: 0,
                        },
                        flexWrap: 'nowrap',
                      },
                    }}
                  />
                  {isAction && (
                    <SwitchTable
                      disablePagination
                      disableSort
                      showActionsColumn
                      btnText={t(`timeLinesCreateOrEdit.addAction`)}
                      columns={CONTEXT_MENU_POINT_ACTION_COLUMNS}
                      rows={watchedActions || []}
                      showDialog={showAddActionDialog}
                      type={'context_menu_point_action'}
                      containerSx={{
                        '.MuiTableBody-root > div': {
                          height: '400px !important',
                        },
                      }}
                      onDelete={handleDeleteAction}
                      onShowDialog={handleOpenAddActionDialog}
                      onEdit={params => {
                        handleOpenAddActionDialog('edit', params.row.original.code)
                      }}
                    />
                  )}
                </Grid>
              )}
            </FormProvider>
          </Box>
        </ModalFormContainer>
      </ModalWrapper>
    </PageContext.Provider>
  )
}
